import { CompanyNode, Shareholder } from '../types';
import {
  PendingEdit,
  EditFieldPath,
  ApplyResult,
  ValidationError,
  PendingAddValue,
} from './pendingEdits.types';

const SHAREHOLDER_FIELD_RE = /^shareholders\.(\d+)\.(name|percentage)$/;

export function isFieldStructural(field: EditFieldPath): boolean {
  if (field === 'name') return true;
  const m = SHAREHOLDER_FIELD_RE.exec(field);
  if (m && m[2] === 'name') return true;
  return false;
}

function setNodeField(
  node: CompanyNode,
  field: EditFieldPath,
  value: any
): CompanyNode {
  if (field === 'name') {
    return { ...node, name: typeof value === 'string' ? value : String(value ?? '') };
  }
  if (field === 'country') {
    return { ...node, country: typeof value === 'string' ? value : String(value ?? '') };
  }
  if (field === 'directors') {
    let directors: string[];
    if (Array.isArray(value)) {
      directors = value.map((d) => String(d).trim()).filter((d) => d.length > 0);
    } else if (typeof value === 'string') {
      directors = value
        .split(/[;,]/)
        .map((d) => d.trim())
        .filter((d) => d.length > 0);
    } else {
      directors = [];
    }
    return { ...node, directors };
  }
  const m = SHAREHOLDER_FIELD_RE.exec(field);
  if (m) {
    const idx = parseInt(m[1], 10);
    const sub = m[2] as 'name' | 'percentage';
    const shareholders: Shareholder[] = Array.isArray(node.shareholders)
      ? node.shareholders.map((s) => ({ ...s }))
      : [];
    if (!shareholders[idx]) {
      shareholders[idx] = { name: '', percentage: 0, isExternal: false };
    }
    if (sub === 'name') {
      shareholders[idx].name = typeof value === 'string' ? value : String(value ?? '');
    } else {
      const num = typeof value === 'number' ? value : Number(value);
      shareholders[idx].percentage = isNaN(num) ? 0 : num;
    }
    return { ...node, shareholders };
  }
  return node;
}

function buildNewNode(nodeId: string, v: PendingAddValue): CompanyNode {
  return {
    id: nodeId,
    parentId: null,
    name: v.name,
    country: v.country,
    directors: v.directors,
    shareholders: Array.isArray(v.shareholders) ? v.shareholders : [],
    ownershipInfo: '',
    position: v.country,
    depth: 0,
  };
}

export function applyPendingEdits(
  nodes: CompanyNode[],
  pending: PendingEdit[]
): ApplyResult {
  const errors: ValidationError[] = [];

  // Build mutable map of nodes
  const nodeMap = new Map<string, CompanyNode>();
  nodes.forEach((n) => nodeMap.set(n.id, { ...n, shareholders: [...n.shareholders] }));

  // Validation pass 1: percentage range checks (these don't depend on final state)
  for (const p of pending) {
    if (p.type === 'edit' && p.field && p.field.endsWith('.percentage')) {
      const num = Number(p.value);
      if (isNaN(num) || num < 0 || num > 100) {
        errors.push({
          nodeId: p.nodeId,
          field: p.field,
          message: 'Percentage moet tussen 0-100 liggen',
          level: 'error',
        });
      }
    }
  }

  // Apply mutations
  for (const p of pending) {
    if (p.type === 'edit') {
      const node = nodeMap.get(p.nodeId);
      if (!node || !p.field) continue;
      nodeMap.set(p.nodeId, setNodeField(node, p.field, p.value));
    } else if (p.type === 'delete') {
      nodeMap.delete(p.nodeId);
    } else if (p.type === 'add') {
      const v = p.value as PendingAddValue;
      if (v) {
        nodeMap.set(p.nodeId, buildNewNode(p.nodeId, v));
      }
    }
  }

  // Validation pass 2: name validity (post-edit final state)
  // External shareholder nodes (country='EXT' of id startend met 'ext-')
  // worden uitgesloten van uniekheidscheck — die zijn shareholder-references,
  // niet zelfstandige entiteiten, en mogen dezelfde naam hebben als een
  // bedrijf wanneer dat bedrijf niet in de dataset zit als regulier company.
  const finalNames = new Map<string, string>();
  const isExternalNodeId = (id: string, n: CompanyNode | undefined): boolean =>
    id.startsWith('ext-') || (n?.country === 'EXT');
  nodeMap.forEach((n, id) => {
    if (isExternalNodeId(id, n)) return; // skip externals
    finalNames.set(id, n.name);
  });

  // Empty name check (alleen voor reguliere bedrijven)
  finalNames.forEach((name, id) => {
    if (!name || !name.trim()) {
      errors.push({
        nodeId: id,
        field: 'name',
        message: 'Bedrijfsnaam is verplicht',
        level: 'error',
      });
    }
  });

  // Uniqueness check — alleen tussen reguliere bedrijven
  const nameToIds = new Map<string, string[]>();
  finalNames.forEach((name, id) => {
    const key = (name || '').trim();
    if (!key) return;
    const arr = nameToIds.get(key) || [];
    arr.push(id);
    nameToIds.set(key, arr);
  });

  nameToIds.forEach((ids, name) => {
    if (ids.length > 1) {
      ids.forEach((id) => {
        errors.push({
          nodeId: id,
          field: 'name',
          message: `Bedrijfsnaam moet uniek zijn (botst met "${name}")`,
          level: 'error',
        });
      });
    }
  });

  const hasStructuralChanges = pending.some((p) => p.isStructural);

  // Reconcile externals: shareholders die naar een naam verwijzen die NIET
  // bestaat als bedrijf én geen ext- node is, worden automatisch aangemaakt
  // als externe aandeelhouder. Mirrors ExcelProcessor.processCompanyData.
  const finalNodes = reconcileExternalShareholders(Array.from(nodeMap.values()));

  return {
    nodes: finalNodes,
    hasStructuralChanges,
    errors,
  };
}

/**
 * Voor elke shareholder-naam die niet als bedrijf voorkomt en niet al een
 * `ext-` node heeft, maak automatisch een externe shareholder node aan met
 * country='EXT'. Behoudt bestaande externals (cleanupt geen orphans — dat
 * voorkomt dat handmatige customizations verloren gaan).
 */
function reconcileExternalShareholders(nodes: CompanyNode[]): CompanyNode[] {
  const companyNames = new Set<string>();
  const externalNames = new Set<string>();
  let maxNumericId = 0;

  for (const n of nodes) {
    const isExternal = n.country === 'EXT' || n.id.startsWith('ext-');
    if (isExternal) {
      externalNames.add(n.name);
    } else {
      companyNames.add(n.name);
    }
    const numId = parseInt(n.id, 10);
    if (Number.isFinite(numId) && numId > maxNumericId) maxNumericId = numId;
    const extMatch = n.id.match(/^ext-(\d+)$/);
    if (extMatch) {
      const en = parseInt(extMatch[1], 10);
      if (Number.isFinite(en) && en > maxNumericId) maxNumericId = en;
    }
  }

  const newExternals: CompanyNode[] = [];
  const referencedShareholderNames = new Set<string>();
  for (const n of nodes) {
    for (const sh of n.shareholders || []) {
      if (!sh.name) continue;
      referencedShareholderNames.add(sh.name);
    }
  }

  let nextId = maxNumericId + 1;
  for (const name of Array.from(referencedShareholderNames)) {
    if (companyNames.has(name)) continue; // bestaat als bedrijf
    if (externalNames.has(name)) continue; // bestaat al als ext-
    const newId = `ext-${nextId++}`;
    newExternals.push({
      id: newId,
      parentId: null,
      name,
      country: 'EXT',
      shareholders: [],
      ownershipInfo: 'External Shareholder',
      position: 'External',
      depth: 0,
      isExpanded: true,
      isHidden: false,
      isVirtualRoot: false,
    });
  }

  // Update shareholder.isExternal flag voor consistentie
  const updatedNodes = nodes.map((n) => {
    if (!n.shareholders || n.shareholders.length === 0) return n;
    const updatedShareholders = n.shareholders.map((sh) => ({
      ...sh,
      isExternal: !companyNames.has(sh.name),
    }));
    return { ...n, shareholders: updatedShareholders };
  });

  return [...updatedNodes, ...newExternals];
}
